import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { customersApi, type CustomerSearchResult } from '../../api/customers.api.js';
import { inboundPaymentsApi } from '../../api/inbound-payments.api.js';

/**
 * Typing in a payment that arrived at a bank we operate by hand.
 *
 * Everywhere else in this system a payment record exists because a partner told
 * us about it. Here it exists because a person read the bank's own screen and
 * typed what they saw. That single difference is what the dialog has to guard.
 *
 * Two consequences. It does not credit anybody — the payment lands in the same
 * review queue as every other, so recording that money arrived and deciding a
 * customer may have it stay two decisions taken by two people. And the bank's
 * own reference is required, because two members of staff working the same
 * statement is an ordinary event and nothing else in the system would notice.
 *
 * Only accounts at a manual bank are offered. For an integrated provider the
 * partner is the witness, and a hand-typed payment there is one that will never
 * reconcile against anything.
 */

/** Accounts at a bank with nothing behind it but our own operators. */
const MANUAL_MECHANICS = new Set(['MANUAL_SHARED']);

/** "12.34" / "12,34" / "12" → minor units. Refuses anything else. */
function toCents(input: string): number | null {
  const cleaned = input.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [whole, frac = ''] = cleaned.split('.');
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  return cents > 0 ? cents : null;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface RecordManualPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onRecorded: (message: string) => void;
}

export function RecordManualPaymentDialog({
  open,
  onClose,
  onRecorded,
}: RecordManualPaymentDialogProps): JSX.Element {
  const [customerQuery, setCustomerQuery] = useState('');
  const [customer, setCustomer] = useState<CustomerSearchResult | null>(null);
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [payerName, setPayerName] = useState('');
  const [paymentDetails, setPaymentDetails] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [valueDate, setValueDate] = useState(today);
  const [failure, setFailure] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['manual-payment-customers', customerQuery],
    queryFn: () => customersApi.search(customerQuery, 20),
    enabled: open && customerQuery.trim().length >= 2,
  });

  const chosen = useQuery({
    queryKey: ['manual-payment-accounts', customer?.customerId ?? ''],
    queryFn: () => customersApi.getCustomer(customer?.customerId ?? ''),
    enabled: open && customer !== null,
  });

  const manualAccounts = (chosen.data?.accounts ?? []).filter((a) =>
    MANUAL_MECHANICS.has(a.provider),
  );
  const account = manualAccounts.find((a) => a.accountId === accountId) ?? null;

  const reset = (): void => {
    setCustomerQuery('');
    setCustomer(null);
    setAccountId('');
    setAmount('');
    setPayerName('');
    setPaymentDetails('');
    setBankReference('');
    setValueDate(today());
    setFailure(null);
  };

  const dismiss = (): void => {
    reset();
    onClose();
  };

  const record = useMutation({
    mutationFn: (input: {
      provider: string;
      customerId: string;
      accountId: string;
      amountCents: number;
      currency: string;
      payerName: string;
      paymentDetails: string;
      bankReference: string;
      valueDate: string;
    }) => inboundPaymentsApi.recordManual(input),
    onSuccess: () => {
      const message = `Recorded. It is now in the queue awaiting release — nobody has been credited.`;
      reset();
      onRecorded(message);
    },
    onError: (err: unknown) => {
      setFailure(err instanceof Error ? err.message : 'The payment could not be recorded.');
    },
  });

  const cents = toCents(amount);
  const incomplete =
    customer === null ||
    account === null ||
    cents === null ||
    payerName.trim().length < 2 ||
    bankReference.trim().length < 3 ||
    valueDate.trim() === '';

  const submit = (): void => {
    if (customer === null || account === null || cents === null) return;
    setFailure(null);
    record.mutate({
      provider: account.provider,
      customerId: customer.customerId,
      accountId: account.accountId,
      amountCents: cents,
      currency: account.currency,
      payerName: payerName.trim(),
      paymentDetails: paymentDetails.trim(),
      bankReference: bankReference.trim(),
      valueDate,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={record.isPending ? undefined : dismiss}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Record a payment that arrived</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            For a bank we operate by hand, where no partner tells us money arrived. Recording it
            here does <strong>not</strong> credit the customer — it joins the review queue, and
            somebody else releases it.
          </Alert>

          <TextField
            label="Find the customer"
            value={customerQuery}
            onChange={(e) => setCustomerQuery(e.target.value)}
            helperText="Name, email or phone — at least two characters."
          />
          <TextField
            select
            label="Customer"
            value={customer?.customerId ?? ''}
            onChange={(e) => {
              setCustomer(
                (options.data ?? []).find((c) => c.customerId === e.target.value) ?? null,
              );
              setAccountId('');
            }}
            disabled={customerQuery.trim().length < 2}
            helperText={
              options.isFetching
                ? 'Searching…'
                : (options.data ?? []).length === 0 && customerQuery.trim().length >= 2
                  ? 'Nobody matched that.'
                  : 'Whose money this is.'
            }
          >
            {(options.data ?? []).map((c) => (
              <MenuItem key={c.customerId} value={c.customerId}>
                {c.maskedName} — {c.maskedEmail}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Account the money reached"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            disabled={customer === null || chosen.isLoading}
            helperText={
              customer === null
                ? 'Pick the customer first.'
                : manualAccounts.length === 0 && !chosen.isLoading
                  ? 'This customer has no account at a manual bank. For an integrated provider the partner reports the payment.'
                  : 'The currency comes from the account.'
            }
          >
            {manualAccounts.map((a) => (
              <MenuItem key={a.accountId} value={a.accountId}>
                {a.iban} — {a.currency}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={amount.trim() !== '' && cents === null}
              helperText={
                amount.trim() !== '' && cents === null
                  ? 'Digits and at most two decimals.'
                  : account === null
                    ? 'As it appears on the statement.'
                    : `In ${account.currency}, as it appears on the statement.`
              }
              sx={{ flex: 1 }}
            />
            <TextField
              label="Value date"
              type="date"
              value={valueDate}
              onChange={(e) => setValueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="The date the bank credited us."
              sx={{ flex: 1 }}
            />
          </Stack>

          <TextField
            label="Who sent it"
            value={payerName}
            onChange={(e) => setPayerName(e.target.value)}
            helperText="The name on the transfer."
          />
          <TextField
            label="The bank's own reference"
            value={bankReference}
            onChange={(e) => setBankReference(e.target.value)}
            helperText="Required, and the only thing that stops the same payment being entered twice."
          />
          <TextField
            label="What the sender wrote"
            multiline
            minRows={2}
            value={paymentDetails}
            onChange={(e) => setPaymentDetails(e.target.value)}
            helperText="The payment reference or message, if there was one."
          />

          {account !== null && cents !== null && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Recording {(cents / 100).toFixed(2)} {account.currency} against {account.iban}. No
                fee is taken now — it is priced when the payment is released.
              </Typography>
            </Box>
          )}

          {failure !== null && <Alert severity="error">{failure}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={dismiss} disabled={record.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={incomplete || record.isPending}
        >
          Record it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
